import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Text } from 'rebass/styled-components';
import styled from 'styled-components';
import { space } from 'styled-system';
import { FiLogIn } from 'react-icons/fi';
import qs from 'querystring';
import api from '../api';
import { authConfig, createAuthState } from '../api/auth';
import {
  InputField, Loading, ProductLogo,
  Button,
} from '../components';
import { useAppState, useNotification } from '../hooks';

const LoginWrapper = styled(Flex)`
  overflow: hidden;
`;

const FormWrapper = styled(Flex)`
  border-radius: ${(props) => props.theme.radius.medium};
  box-shadow: ${(props) => props.theme.shadows.small};
`;

const Form = styled.form`
  flex: 1;
`;

const Icon = styled(FiLogIn)`
  stroke-width: 1.5px;
  ${space}
`;

function Login(props) {
  const {
    location,
    setIsAuthed,
    setUserId,
    history,
    match,
  } = props;

  const { accountId: accId, setAccountId } = useAppState();
  const { addNotification } = useNotification();

  // trim whitespaces from both sides of brandId
  // since SSO doesn't work with whitespaces
  const brandId = accId.trim();

  const isMounted = useRef(true);

  const [isAutoLogin, setIsAutoLogin] = useState(true);

  const handleBrandIdChange = (event) => setAccountId(event.target.value);

  const pathFrom = (location?.state?.from?.pathname) || '';

  async function redirectToSSO(accountId) {
    const searchParams = new URLSearchParams();
    searchParams.set('v', '1.0');
    searchParams.set('response_type', 'code');
    searchParams.set('redirect_uri', authConfig.redirectUri);

    // Put accountId and pathFrom together using ,
    // to separate 2 piece of information since JSON string doesn't work.
    // Needs to replace / with something else (use _ below) since SSO API rejects /.
    searchParams.set('state', createAuthState({
      accountId,
      path: pathFrom,
    }));

    searchParams.set('client_id', authConfig.clientId);

    try {
      const baseUrl = await api.auth.getSentinelBaseUrl(accountId);
      window.location.href = `${baseUrl}/sentinel/api/account/${accountId}/authorize?${searchParams.toString()}`;
    } catch (error) {
      if (isMounted.current) {
        if (error?.response?.status === 404) {
          addNotification('Account ID does not exist.', 'error');
        } else {
          addNotification(error, 'error');
        }
      }
    }
  }

  const login = async () => {
    try {
      const accountId = localStorage.getItem('accountId');

      if (!accountId) {
        throw new Error('no accountId');
      }

      const authResult = await api.auth.verify(accountId);

      // These setters are in the main app page, so there is no risk
      // of setting after unmount of this component
      // no need to check isMounted ref
      setIsAuthed(true);
      setAccountId(authResult.accountId);
      setUserId(authResult.userId);
      const locationFrom = location?.state?.from;
      const pathname = (locationFrom?.pathname) || '/';
      history.replace(pathname);
    } catch (error) {
      if (isMounted.current) {
        setIsAutoLogin(false);
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await redirectToSSO(brandId);
  };

  useEffect(() => {
    let accountId = match?.params?.accountId;

    if (!accountId) {
      try {
        const stateSearch = location?.state?.from?.search;
        if (stateSearch) {
          const { accountId: qsAccountId } = qs.parse(stateSearch.slice(1));
          if (/^[a-z0-9]+$/gi.test(qsAccountId)) {
            accountId = qsAccountId;
          }
        }
      } catch (err) {
        // do nothing
      }
    }

    if (accountId) {
      redirectToSSO(accountId);
    } else {
      login();
    }

    return () => {
      isMounted.current = false;
    };
  }, []); // eslint-disable-line

  if (isAutoLogin) {
    return (
      <Flex flex={1} alignItems="center" justifyContent="center">
        <Loading size={24} data-testid="loading-login" />
      </Flex>
    );
  }

  return (
    <LoginWrapper justifyContent="center" alignItems="center" flex={1}>
      <Flex flexDirection="column" alignItems="center">
        <Box pb={4} sx={{ maxWidth: 380 }}>
          <ProductLogo
            logoWidth="70px"
            logoHeight="80px"
            fontSize={6}
            fontWeight={100}
            sx={{
              color: 'lightgrey',
            }}
            ml={3}
          />
        </Box>
        <Text mb={3} mt={2} fontSize={4} fontWeight={200} alignSelf="flex-start" variant="title">
          Please enter Account ID
        </Text>
        <FormWrapper
          justifyContent="center"
          alignItems="center"
          p={4}
          width="380px"
          mb={5}
          variant="container"
        >
          <Form onSubmit={handleSubmit}>
            <InputField
              label="Account ID"
              name="brandId"
              id="brandInput"
              value={brandId}
              handleChange={handleBrandIdChange}
              required
            />
            <Button
              data-testid="login-button"
              id="loginButton"
              variant="primary"
              type="submit"
            >
              <Box>Next</Box>
              <Icon ml={2} />
            </Button>
          </Form>
        </FormWrapper>
      </Flex>
    </LoginWrapper>
  );
}

Login.propTypes = {
  location: PropTypes.object.isRequired,
  setIsAuthed: PropTypes.func.isRequired,
  setUserId: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default Login;
